import React from 'react'
import clsx from 'clsx'
import { useDebounce } from 'react-use'

import { Avatar } from '@wartek-id/avatar'
import { useToast } from '@wartek-id/toast'

import { useAuth } from 'utils/auth'
import { RenderMD } from 'components/RenderMD'
import MessageFeedback from 'app/ConversationPage/components/MessageFeedback'
import MessageOptions from 'app/ConversationPage/components/MessageOptions'
import {
  MessageFeedbackActionSheet,
  MessageLikeDislikeActionSheet,
} from 'app/ConversationPage/components/MessageFeedbackActionSheet'
import {
  COPY_TO_CLIPBOARD_TOAST_MESSAGE,
  GURU_AI_AVATAR_IMAGE_PATH,
} from 'app/ConversationPage/constants'
import {
  useConversationStore,
  useConversationMessageStore,
} from 'stores/conversation'
import { useSETrackEvent } from 'app/ConversationPage/hooks/useSETrackEvent'

interface IConversationMessageProps {
  index: number
  mode?: 'client' | 'server'
  messageTokens?: string[]
  messageId?: string
  isLastItem?: boolean
}
const ConversationMessage: React.FC<IConversationMessageProps> = ({
  index,
  mode,
  messageId,
  messageTokens,
  isLastItem,
}) => {
  const [localMessage, setLocalMessage] = React.useState<string[]>([])
  const toast = useToast()
  const { session } = useAuth()
  const isClient = React.useMemo(() => mode === 'client', [mode])
  const isServer = React.useMemo(() => mode === 'server', [mode])
  const { conversationId } = useConversationStore()
  const { isConnecting, isStreaming } = useConversationMessageStore()
  const [isActionSheetOpen, setActionSheetOpen] = React.useState<boolean>(false)
  const [likeDislikeAction, setLikeDislikeAction] = React.useState<string>('')
  const { SETrackConversationPage } = useSETrackEvent()

  const copyToClipboardHandler = () => {
    toast({
      message: COPY_TO_CLIPBOARD_TOAST_MESSAGE,
      type: 'success',
    })
  }

  const trackLikeDislikeAction = (action: string) => {
    SETrackConversationPage('GURU_AI_LIKE_DISLIKE', {
      ai_chat_session_id: conversationId,
      ai_message_id: messageId,
      page_element: action,
    })
  }

  const likeActionHandler = () => {
    setLikeDislikeAction('like')
    setActionSheetOpen(false)
    trackLikeDislikeAction('LIKE')
  }

  const dislikeActionHandler = () => {
    setLikeDislikeAction('dislike')
    setActionSheetOpen(false)
    trackLikeDislikeAction('DISLIKE')
  }

  const isFinishRendering = React.useMemo(
    () => messageTokens.length === localMessage.length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messageTokens.length, localMessage.length]
  )

  React.useEffect(() => {
    if (!isServer) {
      setLocalMessage(messageTokens)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isServer, messageTokens.length])

  useDebounce(
    () => {
      if (isServer) {
        setLocalMessage([
          ...localMessage,
          ...messageTokens.slice(localMessage.length, messageTokens.length),
        ])
      }
    },
    20,
    [isServer, messageTokens.length]
  )

  return (
    <section className={isServer ? 'bot' : 'user'}>
      <div
        className={clsx('p-4', isServer && 'bg-white', isClient && 'bg-gray-0')}
      >
        <div className="flex items-start space-x-4">
          <div className="flex-none">
            <Avatar
              src={isServer ? GURU_AI_AVATAR_IMAGE_PATH : session.user.picture}
              name={isServer ? 'Asisten Guru' : session.user.name}
              size="md"
              alt={`${isServer ? 'Bot' : 'User'} avatar`}
            />
          </div>
          <div>
            <RenderMD>{localMessage.join('')}</RenderMD>
          </div>
        </div>

        {isClient || (isServer && !isLastItem) ? (
          <MessageOptions
            onClick={() => {
              setActionSheetOpen(true)
            }}
          />
        ) : null}
      </div>

      {isServer &&
      isLastItem &&
      !isConnecting &&
      !isStreaming &&
      isFinishRendering ? (
        <MessageFeedback
          index={index}
          message={localMessage.join('')}
          onCopy={copyToClipboardHandler}
          onLike={likeActionHandler}
          onDislike={dislikeActionHandler}
        />
      ) : null}

      <MessageFeedbackActionSheet
        isOpen={isActionSheetOpen}
        mode={mode}
        message={localMessage.join('')}
        onClose={() => {
          setActionSheetOpen(false)
        }}
        onCopy={copyToClipboardHandler}
        onLike={likeActionHandler}
        onDislike={dislikeActionHandler}
      />

      <MessageLikeDislikeActionSheet
        messageId={messageId}
        isOpen={Boolean(likeDislikeAction)}
        action={likeDislikeAction}
        onClose={() => {
          setLikeDislikeAction('')
        }}
      />
    </section>
  )
}

export default ConversationMessage
